import { selectUserInfo } from "features/user/userSlice";
import { Box, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { BrandingResponse } from "subModule/src/services/allcommon/interface";
import { themes } from "theme/themes";
import { useEffect, useMemo, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import { useSnackbar } from "notistack";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import ServiceOnTime from "components/KPIs/ServiceOnTime";
import SoSReport from "components/KPIs/SoSReport";
import FleetAvailability from "components/KPIs/FleetAvailability";
import VehicleTurnaroundTime from "components/KPIs/VehicleTurnaroundTime";
import TechnicianProductivity from "components/KPIs/TechnicianProductivity";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import DashboardPageTitle from "components/Analytics/DashboardPageTitle";

export default function KpiDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const [stationsLoaded, setStationsLoaded] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [globalFilterApplied, setGlobalFilterApplied] =
    useState<boolean>(false);
  const { control, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      globalFilters: {
        range: { value: "monthly", label: t("mtd") },
        allStation: false,
      },
    },
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const firstRender = useRef(true);
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: pageNo,
      size: 50,
      name: dropdownSearchTerm,
    });
  const themeColors =
    (branding as BrandingResponse)?.attributes?.themeId === 100
      ? (branding as BrandingResponse)?.attributes?.customTheme
      : (themes as any)[
          (branding as BrandingResponse)?.attributes?.themeId || 3
        ];
  const handleStationActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => stationsRefetch().finally(() => setSearchLoading(null)),
      300,
    );
  };
  useEffect(() => {
    const getStationsData = setTimeout(async () => {
      await stationsRefetch();
      setStationsLoaded(true);
    }, 50);
    return () => clearTimeout(getStationsData);
  }, []);
  const allStation = 0;
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  if ((stationsData?.pages[0]?.data?.content?.length ?? 0) > 1)
    stations.splice(0, 0, { value: allStation, label: t("allStations") });
  const durationOptions = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  useEffect(() => {
    if (firstRender.current && stationsLoaded && stations.length > 0) {
      firstRender.current = false;
      const defaultStation =
        (stations.length > 1 ? stations[1] : stations[0]) || null;
      setValue("globalFilters.stationId", defaultStation);
    }
  }, [stationsLoaded, stations]);

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        className="mb-[-8px] sticky top-[43px] z-10"
        style={{ background: themeColors?.appBg || "#F1F6F9" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} lg={5}>
            <DashboardPageTitle
              title={t("servicekpi")}
              color={themeColors?.primary}
            />
          </Grid>
          <Grid item xs={12} md={8} lg={7}>
            <Grid container spacing={2} className="justify-end pt-2">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.stationId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={stations}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        if (selectedOption?.value === 0)
                          setValue("globalFilters.allStation", true);
                        else setValue("globalFilters.allStation", false);
                        setGlobalFilterApplied(true);
                      }}
                      isLoading={searchLoading === "globalFilters"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleStationActions(
                            "globalFilters",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleStationActions(
                              "globalFilters",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(stationsData?.pages[0]?.data as any).last)
                          handleStationActions("globalFilters", "loadNext");
                      }}
                      page={pageNo}
                      defaultPlaceholder={t("stations")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.range"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={durationOptions}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setGlobalFilterApplied(true);
                      }}
                      defaultPlaceholder={t("duration")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        {checkPrivileges("ROLE_KPI_ANALYTICS_SERVICE_TIME") && (
          <ServiceOnTime
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleStationActions={handleStationActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6} sx={{ display: "flex" }}>
        {(checkPrivileges("ROLE_KPI_ANALYTICS_TECHNICIAN_PRODUCTIVITY") ||
          checkPrivileges("ROLE_KPI_ANALYTICS_VIEW")) && (
          <Box sx={{ width: "100%" }}>
            <TechnicianProductivity
              user={loggedInUserData}
              themes={themeColors}
              stations={stations as { id: number; name: string }[] | []}
              handleStationActions={handleStationActions}
              globalFilters={watch("globalFilters")}
              {...{
                enqueueSnackbar,
                stationsLoaded,
                dropdownSearchTerm,
                setDropdownSearchTerm,
                pageNo,
                searchLoading,
                setPageNo,
                stationsData,
                globalFilterApplied,
                setGlobalFilterApplied,
              }}
            />
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={5} sx={{ display: "flex" }}>
        {checkPrivileges("ROLE_KPI_ANALYTICS_SOS") && (
          <Box sx={{ width: "100%" }}>
            <SoSReport
              user={loggedInUserData}
              themes={themeColors}
              stations={stations as { id: number; name: string }[] | []}
              handleStationActions={handleStationActions}
              globalFilters={watch("globalFilters")}
              {...{
                enqueueSnackbar,
                stationsLoaded,
                dropdownSearchTerm,
                setDropdownSearchTerm,
                pageNo,
                searchLoading,
                setPageNo,
                stationsData,
                globalFilterApplied,
                setGlobalFilterApplied,
              }}
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={7} lg={7}>
        {checkPrivileges("ROLE_KPI_ANALYTICS_FLEET_AVAILABILITY") && (
          <FleetAvailability
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleStationActions={handleStationActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        )}
      </Grid>
      <Grid item xs={12} md={8} lg={5} sx={{ display: "flex" }}>
        {checkPrivileges("ROLE_KPI_ANALYTICS_VEHICLE_TURNAROUND") && (
          <Box sx={{ width: "100%" }}>
            <VehicleTurnaroundTime
              user={loggedInUserData}
              themes={themeColors}
              stations={stations as { id: number; name: string }[] | []}
              handleStationActions={handleStationActions}
              globalFilters={watch("globalFilters")}
              {...{
                enqueueSnackbar,
                stationsLoaded,
                dropdownSearchTerm,
                setDropdownSearchTerm,
                pageNo,
                searchLoading,
                setPageNo,
                stationsData,
                globalFilterApplied,
                setGlobalFilterApplied,
              }}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
